import type { StyleSpecification } from "maplibre-gl";

import {
  DEFAULT_TILES_SERVICE,
  MIN_TRACKING_POINTS_FOR_POLYLINE,
} from "../constants/common.const";
import { DirectionEnum, TilesServiceEnum } from "../enums";
import { TLineStringFeature } from "../models/map.models";
import { decodeRoutePolylinePositions } from "./map-geometry.helpers";
import { getGoogleTileUrl, getMapboxTileUrl } from "./tiles.helpers";

/**
 * Decode an encoded route polyline to `[lat, lng]` pairs.
 *
 * The copy is the point. `decodeRoutePolylinePositions` memoises its result, so handing that array
 * straight to a caller would let one of them mutate every other caller's copy — and callers here go on to
 * reverse and offset the positions. It once also narrowed a Leaflet tuple whose third element (altitude)
 * was optional; that type is gone, but the copy still earns its keep.
 */
export const decodeRouteLatLngPairs = (
  routePolyline: string,
): [number, number][] =>
  decodeRoutePolylinePositions(routePolyline).map(([lat, lng]) => [lat, lng]);

/** OSM raster tiles. MapLibre needs a concrete host — it does not expand a subdomain placeholder. */
export const getOsmTileUrlForMapLibre = (): string =>
  "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

export const buildMapLibreRasterStyle = ({
  tilesService,
  googleSessionToken,
  googleApiKeyValue,
  mapboxToken,
}: {
  tilesService?: TilesServiceEnum;
  googleSessionToken?: string;
  googleApiKeyValue?: string;
  mapboxToken?: string;
}): StyleSpecification => {
  const resolvedService = tilesService ?? DEFAULT_TILES_SERVICE;

  if (
    resolvedService === TilesServiceEnum.Google &&
    googleSessionToken &&
    googleApiKeyValue
  ) {
    return buildRasterStyleSpec({
      tiles: [
        getGoogleTileUrl({
          session: googleSessionToken,
          apiKey: googleApiKeyValue,
        }),
      ],
      attribution: "© Google",
    });
  }

  if (resolvedService === TilesServiceEnum.Mapbox && mapboxToken) {
    return buildRasterStyleSpec({
      tiles: [getMapboxTileUrl({ accessToken: mapboxToken })],
      attribution: "© Mapbox",
    });
  }

  return buildRasterStyleSpec({
    tiles: [getOsmTileUrlForMapLibre()],
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  });
};

const buildRasterStyleSpec = ({
  tiles,
  attribution,
}: {
  tiles: string[];
  attribution: string;
}): StyleSpecification => ({
  version: 8,
  sources: {
    "ryde-raster-tiles": {
      type: "raster",
      tiles,
      tileSize: 256,
      attribution,
    },
  },
  layers: [
    {
      id: "ryde-raster-layer",
      type: "raster",
      source: "ryde-raster-tiles",
      minzoom: 0,
      maxzoom: 22,
    },
  ],
});

/** Leaflet `[lat, lng]` → GeoJSON / MapLibre `[lng, lat]`. */
export const latLngTupleToLngLat = (
  latLng: [number, number],
): [number, number] => [latLng[1], latLng[0]];

export const latLngTuplesToLineStringCoordinates = (
  positions: [number, number][],
): [number, number][] => positions.map(latLngTupleToLngLat);

/** Wrap GeoJSON `[lng, lat]` coordinates in the LineString feature a MapLibre source expects. */
export const toLineStringFeature = (
  coordinates: [number, number][],
): TLineStringFeature => ({
  type: "Feature",
  properties: {},
  geometry: {
    type: "LineString",
    coordinates,
  },
});

/** Same, straight from Leaflet / render-state `[lat, lng]` positions. */
export const latLngTuplesToLineStringFeature = (
  positions: [number, number][],
): TLineStringFeature =>
  toLineStringFeature(latLngTuplesToLineStringCoordinates(positions));

export interface IPlannedRoutePolylineMarker {
  latitude: number;
  longitude: number;
}

export interface IShouldReversePlannedRoutePolylineParams {
  polylinePositions?: [number, number][];
  markerLocations?: IPlannedRoutePolylineMarker[];
}

const squaredDistanceLatLngToMarker = (
  position: [number, number],
  marker: IPlannedRoutePolylineMarker,
): number => {
  const deltaLat = position[0] - marker.latitude;
  const deltaLng = position[1] - marker.longitude;
  return deltaLat * deltaLat + deltaLng * deltaLng;
};

/**
 * Whether a stored route polyline should be reversed before drawing arrows.
 * Prefer geometry: when the polyline start is closer to the last marker than the first, it was saved in
 * course-template order while markers are in ride travel order (e.g. A-course → B-direction ride).
 * Fall back to direction B only when geometry is ambiguous.
 */
export const shouldReversePlannedRoutePolyline = (
  direction?: DirectionEnum,
  params?: IShouldReversePlannedRoutePolylineParams,
): boolean => {
  const { polylinePositions, markerLocations } = params ?? {};

  if (
    polylinePositions &&
    polylinePositions.length >= MIN_TRACKING_POINTS_FOR_POLYLINE &&
    markerLocations &&
    markerLocations.length >= MIN_TRACKING_POINTS_FOR_POLYLINE
  ) {
    const polylineStart = polylinePositions[0];
    const firstMarker = markerLocations[0];
    const lastMarker = markerLocations[markerLocations.length - 1];
    const distToFirst = squaredDistanceLatLngToMarker(
      polylineStart,
      firstMarker,
    );
    const distToLast = squaredDistanceLatLngToMarker(polylineStart, lastMarker);

    if (distToLast < distToFirst) {
      return true;
    }

    if (distToFirst < distToLast) {
      return false;
    }
  }

  return direction === DirectionEnum.DropOff;
};

export const reverseLatLngTuples = (
  positions: [number, number][],
): [number, number][] => [...positions].reverse();

export const reverseLineStringFeature = (
  feature: TLineStringFeature,
): TLineStringFeature => ({
  ...feature,
  geometry: {
    ...feature.geometry,
    coordinates: [...feature.geometry.coordinates].reverse(),
  },
});

/** A MapLibre dash pattern needs at least a dash length and a gap length. */
const DASH_PATTERN_MIN_PARTS = 2;

/**
 * Convert a Leaflet dash string (px, e.g. "10,8" or "10 8") to a MapLibre `line-dasharray`
 * expressed in line-width units. Returns undefined for an empty/invalid value.
 */
export const leafletDashToMapLibreDashArray = (
  leafletDash: string | undefined,
  lineWidthPx: number,
): number[] | undefined => {
  if (!leafletDash || lineWidthPx <= 0) {
    return undefined;
  }

  // Leaflet accepts both separators ("10,8" and "10 8") and both are used in this repo, so split on either.
  // Splitting on commas alone silently returned undefined for the space-separated patterns, which drew every
  // dashed/dotted line solid.
  const parts = leafletDash
    .trim()
    .split(/[\s,]+/)
    .map(part => Number(part) / lineWidthPx)
    .filter(value => Number.isFinite(value) && value >= 0);

  // MapLibre needs a dash/gap pair; a lone value is not a pattern.
  return parts.length >= DASH_PATTERN_MIN_PARTS ? parts : undefined;
};
